import { IconDoubleAltArrowLeft } from '@shared/icons/IconDoubleAltArrowLeft';
import { IconDoubleAltArrowRight } from '@shared/icons/IconDoubleAltArrowRight';
import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useRef, useState } from 'react';

import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import type { MessageType } from '@/hooks/messages';
import { useMessages } from '@/hooks/messages';
import { isSmallScreen, useWindowDimensions } from '@/hooks/useWindowDimensions';

import Modal from '../../Modal';
import { helpModalMessages } from './messages';

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StepProps {
  number: number;
  title: string;
  isActive: boolean;
  onClick: () => void;
  onRef?: (element: HTMLButtonElement | null) => void;
}

const Step: React.FC<StepProps> = ({ number, title, isActive, onClick, onRef }) => (
  <button
    ref={onRef}
    className={`flex min-w-fit cursor-pointer items-center justify-center rounded-lg px-3 py-2 text-xs font-medium text-black transition-colors sm:px-4 sm:py-3 sm:text-sm dark:text-white ${
      isActive ? 'bg-bodyLight dark:bg-c16' : 'dark:bg-c4 hover:dark:bg-c16'
    }`}
    onClick={onClick}
  >
    <span className='whitespace-nowrap'>
      {number}. {title}
    </span>
  </button>
);

const getSteps = (msgs: MessageType<typeof helpModalMessages>) => {
  return [
    {
      title: msgs.aiAdvisoryLabel(),
      description: msgs.aiAdvisoryShortDescription(),
      longDescription: msgs.aiAdvisoryLongDescription(),
    },
    {
      title: msgs.setupLabel(),
      description: msgs.setupShortDescription(),
      longDescription: msgs.setupLongDescription(),
    },
    {
      title: msgs.chooseModeLabel(),
      description: msgs.chooseModeShortDescription(),
      longDescription: msgs.chooseModeLongDescription(),
    },
    {
      title: msgs.masterdataLabel(),
      description: msgs.masterdataShortDescription(),
      longDescription: msgs.masterdataLongDescription(),
    },
    {
      title: msgs.projectPhasesLabel(),
      description: msgs.projectPhasesShortDescription(),
      longDescription: msgs.projectPhasesLongDescription(),
    },
    {
      title: msgs.estimatingLabel(),
      description: msgs.estimatingShortDescription(),
      longDescription: msgs.estimatingLongDescription(),
    },
    {
      title: msgs.resourceLabel(),
      description: msgs.resourceShortDescription(),
      longDescription: msgs.resourceLongDescription(),
    },
    {
      title: msgs.costsLabel(),
      description: msgs.costsShortDescription(),
      longDescription: msgs.costsLongDescription(),
    },
    {
      title: msgs.reviewLabel(),
      description: msgs.reviewShortDescription(),
      longDescription: msgs.reviewLongDescription(),
    },
  ];
};

const TabContent: React.FC<{
  activeTab: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
}> = ({ activeTab, isExpanded, onToggleExpand }) => {
  const modalMessages = useMessages(helpModalMessages);
  const steps = getSteps(modalMessages);
  const step = steps[activeTab] || steps[0];

  return (
    <div
      className={`relative z-10 mb-4 flex w-full flex-col rounded-lg transition-all duration-300 ${isExpanded ? 'min-h-[400px]' : 'h-[250px]'}`}
    >
      <div className='flex w-full items-start justify-between'>
        <div className='mr-4 flex-1 text-left'>
          <div className='text-2xl font-medium' role='heading' aria-level={3}>
            <SanitizedHtml htmlString={step.description} />
          </div>

          {isExpanded && (
            <div className='mt-4 text-base text-gray-600 dark:text-gray-300'>
              <SanitizedHtml htmlString={step.longDescription} />
            </div>
          )}
        </div>
      </div>

      <div className='mt-6 flex justify-start'>
        <button
          onClick={onToggleExpand}
          className='btn btn-outline-primary px-4 py-2 text-sm font-medium'
        >
          {isExpanded
            ? modalMessages.showLessButtonLabel()
            : modalMessages.learnAboutButtonLabel({ currentStep: step.title })}
        </button>
      </div>
    </div>
  );
};

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const modalMessages = useMessages(helpModalMessages);
  const globalMessages = useMessages(GlobalMessages);
  const steps = getSteps(modalMessages);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { width } = useWindowDimensions();

  // Reset expanded state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setActiveTab(0);
    }
  }, [isOpen]);

  // Scroll active step into view when activeTab changes
  useEffect(() => {
    const activeStepElement = stepRefs.current[activeTab];

    // Check if we need to scroll (only on mobile/small screens)
    if (activeStepElement && isSmallScreen(width)) {
      activeStepElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeTab, width]);

  const setStepRef = (index: number) => (element: HTMLButtonElement | null) => {
    stepRefs.current[index] = element;
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleNextClick = () => {
    if (activeTab < steps.length - 1) {
      setActiveTab(activeTab + 1);
    } else {
      onClose();
      setActiveTab(0);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={modalMessages.title({ appName: globalMessages.appName() })}
      className='max-w-5xl'
      buttons={
        <div className='flex w-full'>
          <div className='flex-1'>
            {activeTab > 0 && (
              <button
                className='btn dark:btn-outline-white flex items-center gap-2 px-6 py-3 dark:bg-transparent'
                onClick={() => setActiveTab(activeTab - 1)}
              >
                <IconDoubleAltArrowLeft className='h-6 w-6 text-black dark:text-white' />
                {globalMessages.back()}
              </button>
            )}
          </div>
          <div className='flex justify-end'>
            <button
              className='btn dark:btn-outline-white flex items-center gap-2 px-6 py-3 dark:bg-transparent'
              onClick={handleNextClick}
            >
              {activeTab < steps.length - 1 ? globalMessages.next() : globalMessages.finish()}{' '}
              <IconDoubleAltArrowRight className='h-6 w-6 text-black dark:text-white' />
            </button>
          </div>
        </div>
      }
    >
      <div className='flex flex-col'>
        <div
          ref={scrollContainerRef}
          className='dark:bg-c4 mb-10 flex [scrollbar-width:none] gap-2 overflow-x-auto rounded-lg bg-gray-300 p-2 [-ms-overflow-style:none] sm:flex-wrap [&::-webkit-scrollbar]:hidden'
        >
          {steps.map((step, index) => (
            <Step
              key={step.title}
              number={index + 1}
              title={step.title}
              isActive={activeTab === index}
              onClick={() => setActiveTab(index)}
              onRef={setStepRef(index)}
            />
          ))}
        </div>

        <TabContent
          activeTab={activeTab}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
        />
      </div>
    </Modal>
  );
};

export default HelpModal;
